type InstallEvent = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
let deferred: InstallEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(listener => listener());

export function installHelpKey() {
  const agent = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(agent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'appInstall.helpIos';
  if (/Android/i.test(agent)) return 'appInstall.helpAndroid';
  if (/Macintosh/i.test(agent) && /Safari/i.test(agent) && !/Chrome|Chromium|Edg/i.test(agent)) return 'appInstall.helpMac';
  return 'appInstall.helpDesktop';
}

// The Capacitor apps (mobile/pos, mobile/admin) append this marker to
// the WebView user agent; inside them the app is already installed.
const isNativeShell = () => typeof navigator !== 'undefined' && /TajerGoNative\//.test(navigator.userAgent ?? '');

export function isInstalled() {
  return installed || isNativeShell() || (typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true));
}
export function subscribeInstall(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
export async function requestInstall(): Promise<'accepted' | 'dismissed' | 'manual' | 'failed'> {
  if (!deferred) return 'manual';
  const event = deferred;
  deferred = null;
  try {
    await event.prompt();
    return (await event.userChoice).outcome;
  } catch { return 'failed'; }
  finally { notify(); }
}
export function initializeInstall(worker: string, { reloadOnUpdate = false }: { reloadOnUpdate?: boolean } = {}) {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    deferred = event as InstallEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => { installed = true; deferred = null; notify(); });
  window.matchMedia('(display-mode: standalone)').addEventListener('change', notify);
  if ('serviceWorker' in navigator && window.isSecureContext) {
    const hadController = Boolean(navigator.serviceWorker.controller);
    if (reloadOnUpdate && hadController) {
      navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    }
    const register = () => { void navigator.serviceWorker.register(worker, { scope: '/', updateViaCache: 'none' }).then(registration => registration.update()).catch(() => { /* Online use remains available if installation is unavailable. */ }); };
    if (document.readyState === 'complete') register();
    else window.addEventListener('load', register, { once: true });
  }
}
